/**
 * React Query bindings for the Developer Platform surfaces this app still
 * owns: the "Sign in with Tabbio" consent screen and Settings > Connected
 * apps. Registering an app moved to the developer platform app
 * (platform.tabbio.com).
 */
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ConnectedAppSummary,
  OAuthApproveInput,
  OAuthAuthorizeRequestParams,
  OAuthConsentContext,
  OAuthRedirectResult,
  UpdateConnectedAppInput,
} from '@tabbio/types';
import {
  approveOAuthConsent,
  denyOAuthConsent,
  fetchConnectedApps,
  fetchOAuthConsentContext,
  revokeConnectedApp,
  updateConnectedApp,
} from '../lib/connected-apps-api';
import { buildCvListWithMainFirst } from '../lib/cv-main-default';
import { useCatalogAutoApplyOverview } from './useJobsApi';
import { useTailoredCVs } from './useTailoredCvApi';

const ROOT = 'developer-platform' as const;

export const DEVELOPER_PLATFORM_KEYS = {
  connectedApps: [ROOT, 'connected-apps'] as const,
  consentContext: (fingerprint: string) =>
    [ROOT, 'consent', fingerprint] as const,
};

// ─── Consent screen ───────────────────────────────────────────

/**
 * The consent context is keyed by the whole request, not just the client id:
 * a second authorize request from the same app with different scopes must not
 * read the first one's cached answer.
 */
export function useOAuthConsentContext(
  params: OAuthAuthorizeRequestParams,
  enabled: boolean,
) {
  const fingerprint = [
    params.clientId,
    params.redirectUri,
    params.scope,
    params.codeChallenge,
  ].join('|');

  return useQuery<OAuthConsentContext>({
    queryKey: DEVELOPER_PLATFORM_KEYS.consentContext(fingerprint),
    queryFn: () => fetchOAuthConsentContext(params),
    enabled,
    // A consent decision is made once, right now. Retrying a rejected client id
    // three times just delays the error page.
    retry: false,
    staleTime: 0,
    gcTime: 0,
  });
}

export function useApproveOAuthConsent() {
  const queryClient = useQueryClient();

  return useMutation<OAuthRedirectResult, Error, OAuthApproveInput>({
    mutationFn: approveOAuthConsent,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: DEVELOPER_PLATFORM_KEYS.connectedApps,
      });
    },
  });
}

export function useDenyOAuthConsent() {
  return useMutation<OAuthRedirectResult, Error, OAuthAuthorizeRequestParams>({
    mutationFn: denyOAuthConsent,
  });
}

// ─── Connected apps ───────────────────────────────────────────

export function useConnectedApps(enabled = true) {
  return useQuery<ConnectedAppSummary[]>({
    queryKey: DEVELOPER_PLATFORM_KEYS.connectedApps,
    queryFn: fetchConnectedApps,
    enabled,
    staleTime: 30_000,
  });
}

export function useConnectedApp(connectionId: string | undefined) {
  const query = useConnectedApps(Boolean(connectionId));
  const connection =
    connectionId && query.data
      ? (query.data.find((item) => item.id === connectionId) ?? null)
      : null;

  return { ...query, connection };
}

export function useUpdateConnectedApp(connectionId: string) {
  const queryClient = useQueryClient();

  return useMutation<ConnectedAppSummary, Error, UpdateConnectedAppInput>({
    mutationFn: (input) => updateConnectedApp(connectionId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: DEVELOPER_PLATFORM_KEYS.connectedApps,
      });
    },
  });
}

export function useRevokeConnectedApp() {
  const queryClient = useQueryClient();

  return useMutation<{ revoked: boolean }, Error, string>({
    mutationFn: (connectionId) => revokeConnectedApp(connectionId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: DEVELOPER_PLATFORM_KEYS.connectedApps,
      });
    },
  });
}

// ─── CV picker options (Connected apps detail) ────────────────

/**
 * The CV list the connected-app detail screen offers, main CV first.
 *
 * The consent screen gets its options from the server context; this screen is
 * reached from Settings with no such context, so it composes the same list the
 * create-CV flow builds (`lib/cv-main-default.ts`) and keeps the `master`
 * sentinel the API expects for "follow my main CV".
 */
export function useConnectedAppCvOptions(enabled = true) {
  const cvs = useTailoredCVs(enabled);
  const autoApply = useCatalogAutoApplyOverview(enabled);
  const { t } = useTranslation('seeker');

  const defaultCvId =
    autoApply.data?.rules.find((rule) => rule.key === 'auto-apply')?.settings
      .defaultCvId ?? null;

  const options = useMemo(
    () =>
      buildCvListWithMainFirst(
        cvs.data ?? [],
        defaultCvId,
        t(defaultCvId ? 'cv.list.profileTitle' : 'cv.list.masterTitle'),
      ).map((cv) => ({
        id: cv.id,
        title: cv.title,
        updatedAt:
          typeof cv.updatedAt === 'string'
            ? cv.updatedAt
            : new Date(cv.updatedAt ?? Date.now()).toISOString(),
        isMainCv: cv.isMainCv === true,
      })),
    [cvs.data, defaultCvId, t],
  );

  return { options, isLoading: cvs.isPending || autoApply.isPending };
}
